import { useMemo, useState } from "react";
import type { JsonSchema, UISchemaElement } from "@jsonforms/core";
import type { CatalogOption } from "@/lib/api/generated/casedb";
import type { CaseReviewCollectionApi } from "./useCaseReview";
import type { CaseReviewContextValue } from "../model/caseReview";
import type { CollectionSpec } from "../constants/caseReviewSections";
import type { CaseReviewFilter } from "../constants/caseReviewFilters";
import type { ReviewCounts } from "../model/caseReview";
import { evidenceOf, optionsOf, hasAiReviewColumns } from "../utils/caseReviewFieldState";
import { chipOptionLabel, nestRecordValues, recordDetailLayout, recordScalarSchema } from "../utils/caseReviewRecords";
import { countTallies, withUnsaved } from "../utils/reviewCounts";
import { labelGapsBySchema } from "../utils/reviewCountLabels";
import { caseReviewGroupFields } from "../utils/caseReviewSubsections";

import { DEVICE_PRODUCT_TYPE_LABEL, PRODUCT_TYPE_KEY, WHODD_CODE_FIELD_ID, WHODD_OUT_OF_SCOPE_LABEL } from "../constants/caseReviewLabels";

interface RecordTableView {
  spec: CollectionSpec;
  schema?: JsonSchema;
  layout?: UISchemaElement;
  row?: CaseReviewCollectionApi["rows"][number];
  countsOf: (recordId: string) => ReviewCounts;
  optionLabel: ReturnType<typeof chipOptionLabel>;
  error?: string;
  hasAi?: boolean;
  shown?: UISchemaElement;
  context?: CaseReviewContextValue;
  children?: Array<{ spec: CollectionSpec; api: CaseReviewCollectionApi }>;
  values?: Record<string, unknown>;
  unattached?: number;
}

/** Converts catalog/record state to ready-to-render props; RecordTable only composes UI elements. */
export function useRecordReviewTable(api: CaseReviewCollectionApi, uischema: UISchemaElement,
  fieldOptions: Record<string, CatalogOption[]>, filter: CaseReviewFilter, search: string,
  onRequestEdit?: () => void, activeGroup?: string): RecordTableView {
  const { spec, prefix } = api;
  const layout = useMemo(() => recordDetailLayout(uischema, spec.scope), [uischema, spec.scope]);
  const schema = useMemo(() => recordScalarSchema(api.schema) as JsonSchema | undefined, [api.schema]);
  const rowOptions = useMemo(() => optionsOf(schema, fieldOptions), [schema, fieldOptions]);
  // Which groups are drawn open. Kept here so it survives a chip switch — a reviewer who opened
  // Regulatory Details on one reporter is still reading regulatory details on the next.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [touchedGroups, setTouchedGroups] = useState(false);
  const optionLabel = useMemo(() => chipOptionLabel(schema as Record<string, unknown> | undefined, rowOptions),
    [schema, rowOptions]);
  const keyOf = useMemo(() => (fieldId: string) => fieldId.startsWith(prefix) ? fieldId.slice(prefix.length) : undefined, [prefix]);
  // One record's counts, over all its fields or a group's; names come from the record's schema,
  // since inside a record the record itself needs no naming.
  const countsOver = (recordId: string, keys?: string[]): ReviewCounts => {
    const dirty = api.dirty(recordId).filter((key) => !keys || keys.includes(key));
    return labelGapsBySchema(withUnsaved(countTallies(api.tallies(recordId), keys && new Set(keys)), dirty.length),
      schema, keyOf);
  };
  const countsOf = (recordId: string) => countsOver(recordId);

  const row = api.rows.find((item) => item.recordId === api.selectedId);

  const base = { spec, schema, layout, row, countsOf, optionLabel };
  if (!schema || !layout) return { ...base, error: "This record's catalog schema or layout is missing." };
  if (!row) return base;
  const states = api.states(row.recordId);
  const evidence = evidenceOf(states, keyOf);
  const hasAi = hasAiReviewColumns(evidence, states);
  // Until the reviewer says otherwise, the first group is open and the rest are closed, which is
  // the mock's resting state and the only one that fits a long record on a screen.
  const groups = recordGroups(layout);
  const errors = api.errors(row.recordId);
  // A group holding a field the server refused opens whatever the reviewer had collapsed: a
  // message about a field nobody can see is a message nobody can act on.
  const failed = groups.filter((group) => group.keys.some((key) => errors[`${prefix}${key}`] !== undefined))
    .map((group) => group.label);
  // The group holding the field a navigation went to opens too, so "Next" can show the field
  // (S7, issue 355). A child list's field opens the group that holds the list.
  const navigationKey = api.navigationKey(row.recordId);
  const navigated = navigationKey ? groups.filter((group) => group.keys.some((key) =>
    navigationKey === key || navigationKey.startsWith(`${key}.`))).map((group) => group.label) : [];
  const chosen = touchedGroups ? openGroups : new Set(groups[0] ? [groups[0].label] : []);
  const forced = [...failed, ...navigated];
  const open = forced.length ? new Set([...chosen, ...forced]) : chosen;
  // With a tab naming the group, the layout is narrowed to it and the header rows go away: the
  // tab strip is already the navigation, and a row repeating its name is one heading too many.
  const shown = activeGroup ? groupLayout(layout, activeGroup) : layout;


  const context: CaseReviewContextValue = {
    identityPrefix: row.recordId, caseId: api.caseId, recordId: row.recordId, evidence, options: rowOptions,
    decisions: row.decisions, dirty: api.dirty(row.recordId), selected: api.selectedKey(row.recordId), filter, search,
    focus: (key) => api.selectField(row.recordId, key), changeField: (key, value) => api.changeField(row.recordId, key, value),
    decide: (key, decision) => api.decide(row.recordId, key, decision),
    restoreAiValue: (key) => api.restoreAiValue(row.recordId, key),
    revertDecision: (key) => api.revertDecision(row.recordId, key),
    edit: (key) => api.edit(row.recordId, key), undo: (key) => api.undo(row.recordId, key),
    fieldStates: states, serverErrors: errors, disabledFields: api.disabledFields,
    fieldEditable: api.fieldEditable,
    displayOverrides: optionLabel(row.values)(PRODUCT_TYPE_KEY)?.trim().toLowerCase() === DEVICE_PRODUCT_TYPE_LABEL
      ? { [WHODD_CODE_FIELD_ID]: WHODD_OUT_OF_SCOPE_LABEL } : undefined,
    proposalOf: (fieldId) => api.proposalOf(row.recordId, fieldId),
    countsFor: (keys) => countsOver(row.recordId, keys),
    requestEdit: onRequestEdit, openGroups: open,
    toggleGroup: (label) => {
      setTouchedGroups(true);
      setOpenGroups((current) => {
        const next = new Set(touchedGroups ? current : open);
        if (!next.delete(label)) next.add(label);
        return next;
      });
    },
  };
  const children = (spec.children ?? []).flatMap((child) => {
    const childApi = api.child(row.recordId, child.collectionId);
    return childApi ? [{ spec: child, api: childApi }] : [];
  });
  return { ...base, hasAi, shown, context, children,
    values: validationValues(api, row), unattached: api.unattached(row.recordId) };
}

/** Supplies JSON Forms with the complete draft tree so parent rules can inspect child collections. */
function validationValues(api: CaseReviewCollectionApi, row: CaseReviewCollectionApi["rows"][number]): Record<string, unknown> {
  const values = nestRecordValues(row.values);
  for (const child of api.spec.children ?? []) {
    const childApi = api.child(row.recordId, child.collectionId);
    if (!childApi) continue;
    values[child.collectionId] = childApi.rows.map((childRow) => validationValues(childApi, childRow));
  }
  return values;
}

/** The labelled groups of a record's layout, in the order the catalog authored them. */
function recordGroups(layout: UISchemaElement): Array<{ label: string; keys: string[] }> {
  const node = layout as { type?: string; label?: string; elements?: unknown[] };
  if (node.type === "Group" && node.label) return [{ label: node.label, keys: caseReviewGroupFields(layout) }];
  return ((node.elements ?? []) as UISchemaElement[]).flatMap(recordGroups);
}

/**
 * The contents of one named group, unwrapped.
 *
 * <p>Unwrapped rather than returned as the Group itself, because the tab above already carries the
 * name: keeping the Group would draw its heading a second line below its own tab.
 */
function groupLayout(layout: UISchemaElement, label: string): UISchemaElement {
  const node = layout as { type?: string; label?: string; elements?: unknown[] };
  if (node.type === "Group" && node.label === label) {
    return { type: "VerticalLayout", elements: node.elements ?? [] } as unknown as UISchemaElement;
  }
  for (const element of (node.elements ?? []) as UISchemaElement[]) {
    const found = groupLayout(element, label);
    if ((found as { elements?: unknown[] }).elements?.length) return found;
  }
  return { type: "VerticalLayout", elements: [] } as unknown as UISchemaElement;
}

/** A collection nobody has any records of yet; kept for the spec's own labels. */
export type { CollectionSpec };
